import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { ShoppingBag, User, Menu, X, Facebook, Instagram, Twitter, Linkedin, Youtube } from "lucide-react";
import { useState } from "react";

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, twitter: Twitter, x: Twitter, linkedin: Linkedin, youtube: Youtube };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { settings } = useSettings();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const brand = settings?.business_name || "BOLAWPRESS";
  const tagline = settings?.tagline || "family tradition";
  const footerTagline = settings?.footer_tagline || "Commercial Printing • Nationwide Shipping";
  const copyright = settings?.copyright_text || `© ${new Date().getFullYear()} ${brand}. All rights reserved.`;
  const socials = Object.entries(settings?.social_links || {}).filter(([, url]) => url);
  const emails = (settings?.contact_emails || []).filter(Boolean);
  const phones = (settings?.phone_numbers || []).filter(Boolean);
  const address = settings?.physical_address || "";
  const hours = settings?.business_hours || "";
  const footerText = settings?.footer_text || "Built on a family printing tradition dating back to the 1970s — refined for a modern generation of brands.";

  const link = "text-sm tracking-wide text-[#0a0a0a] hover:text-[#C5A059] transition-colors";
  const links = [
    { to: "/shop", label: "Shop" },
    { to: "/services", label: "Services" },
    { to: "/designer", label: "Designer" },
    { to: "/subscriptions", label: "Subscribe" },
    { to: "/portfolio", label: "Portfolio" },
    { to: "/industries", label: "Industries" },
    { to: "/about", label: "About" },
    { to: "/blog", label: "Blog" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="bg-[#0a0a0a] text-[#FAFAFA] text-xs tracking-[0.3em] uppercase py-2 text-center font-mono" data-testid="announce-bar">
        {footerTagline}
      </div>

      <header className="bg-white/95 backdrop-blur-md border-b border-[#E5E5E5] sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-2" data-testid="nav-logo">
            {settings?.logo_url ? (
              <img src={settings.logo_url} alt={brand} className="h-8 md:h-10 object-contain" data-testid="nav-logo-img" />
            ) : (
              <span className="font-serif text-2xl md:text-3xl tracking-tight text-[#0a0a0a]">{brand}</span>
            )}
            {tagline && <span className="hidden md:inline text-[10px] font-mono tracking-[0.3em] text-[#C5A059] uppercase">{tagline}</span>}
          </Link>
          <nav className="hidden lg:flex items-center gap-8">
            {links.map(l => (
              <Link key={l.to} to={l.to} className={link} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g,'-')}`}>{l.label}</Link>
            ))}
          </nav>
          <div className="flex items-center gap-5">
            <Link to="/quote" className="hidden md:inline text-xs tracking-[0.2em] uppercase font-bold border-b border-[#0a0a0a] pb-1 hover:text-[#C5A059] hover:border-[#C5A059]" data-testid="nav-quote">Request Quote</Link>
            {user ? (
              <div className="relative group">
                <button className="flex items-center gap-2 text-sm" data-testid="nav-account">
                  <User size={18} strokeWidth={1.5} /> <span className="hidden md:inline">{user.name?.split(" ")[0]}</span>
                </button>
                <div className="absolute right-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="w-52 bg-white border border-[#E5E5E5] shadow-sm">
                    <Link to="/dashboard" className="block px-5 py-3 text-sm hover:bg-[#FAFAFA]" data-testid="nav-dashboard">Dashboard</Link>
                    {(user.role === "admin" || user.role === "staff" || user.role === "super_admin") && (
                      <Link to="/admin" className="block px-5 py-3 text-sm hover:bg-[#FAFAFA]" data-testid="nav-admin">Admin</Link>
                    )}
                    <button onClick={() => { logout(); nav("/"); }} className="w-full text-left px-5 py-3 text-sm hover:bg-[#FAFAFA]" data-testid="nav-logout">Logout</button>
                  </div>
                </div>
              </div>
            ) : (
              <Link to="/login" className="text-sm" data-testid="nav-login"><User size={18} strokeWidth={1.5} /></Link>
            )}
            <Link to="/cart" className="relative" data-testid="nav-cart">
              <ShoppingBag size={18} strokeWidth={1.5} />
              {count > 0 && <span className="absolute -top-2 -right-3 bg-[#C5A059] text-[#0a0a0a] text-[10px] font-mono font-bold px-1.5">{count}</span>}
            </Link>
            <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle">{open ? <X /> : <Menu />}</button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t border-[#E5E5E5] bg-white">
            {links.map(l => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block px-6 py-4 border-b border-[#F5F5F5]">{l.label}</Link>
            ))}
            <Link to="/quote" onClick={() => setOpen(false)} className="block px-6 py-4 text-[#C5A059] font-bold">Request Quote</Link>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-[#0a0a0a] text-[#FAFAFA] mt-24 pt-20 pb-10" data-testid="site-footer">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-10">
          <div className="col-span-2">
            <h3 className="font-serif text-3xl mb-3">{brand}</h3>
            <p className="text-sm text-[#888] max-w-sm">{footerText}</p>
            <p className="mt-6 text-xs font-mono tracking-[0.2em] uppercase text-[#C5A059]">{footerTagline}</p>
            {(emails.length > 0 || phones.length > 0 || address || hours) && (
              <div className="mt-8 space-y-1 text-xs text-[#888] font-mono tracking-wider" data-testid="footer-contact-block">
                {emails.map(e => <p key={e}><a href={`mailto:${e}`} className="hover:text-[#C5A059]">{e.toUpperCase()}</a></p>)}
                {phones.map(p => <p key={p}><a href={`tel:${p.replace(/\s+/g,'')}`} className="hover:text-[#C5A059]">{p}</a></p>)}
                {address && <p className="whitespace-pre-line normal-case tracking-normal font-sans">{address}</p>}
                {hours && <p className="normal-case tracking-normal font-sans">{hours}</p>}
              </div>
            )}
            {socials.length > 0 && (
              <div className="mt-6 flex gap-4" data-testid="footer-socials">
                {socials.map(([key, url]) => {
                  const Icon = SOCIAL_ICONS[key] || null;
                  return (
                    <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={key} className="text-[#888] hover:text-[#C5A059] transition-colors" data-testid={`footer-social-${key}`}>
                      {Icon ? <Icon size={18} strokeWidth={1.5} /> : <span className="text-xs uppercase">{key}</span>}
                    </a>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <h4 className="text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059] mb-4">Explore</h4>
            <ul className="space-y-2 text-sm text-[#DADADA]">
              <li><Link to="/shop">Shop</Link></li>
              <li><Link to="/services">Services</Link></li>
              <li><Link to="/portfolio">Portfolio</Link></li>
              <li><Link to="/industries">Industries</Link></li>
              <li><Link to="/blog">Blog</Link></li>
              <li><Link to="/faq">FAQ</Link></li>
              <li><Link to="/contact">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059] mb-4">Legal</h4>
            <ul className="space-y-2 text-sm text-[#DADADA]">
              <li><Link to="/policies/privacy-policy">Privacy Policy</Link></li>
              <li><Link to="/policies/terms">Terms</Link></li>
              <li><Link to="/policies/refund-policy">Refund Policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 mt-16 pt-8 border-t border-[#222] flex flex-col md:flex-row justify-between gap-2 text-xs text-[#666]" data-testid="footer-bottom">
          <p>{copyright}</p>
          {(emails[0] || phones[0]) && (
            <p className="font-mono tracking-[0.2em]">
              {emails[0] && <a href={`mailto:${emails[0]}`} className="hover:text-[#C5A059]">{emails[0].toUpperCase()}</a>}
              {emails[0] && phones[0] && " · "}
              {phones[0] && <a href={`tel:${phones[0].replace(/\s+/g,'')}`} className="hover:text-[#C5A059]">{phones[0]}</a>}
            </p>
          )}
        </div>
      </footer>
    </div>
  );
}
